import Vue from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import absmartly from '@absmartly/javascript-sdk'
import stringHash from '@sindresorhus/string-hash'
import * as Sentry from '@sentry/browser'
import lscache from 'lscache'
import uuid from 'uuid-random'
import parseISO from 'date-fns/parseISO'
import getUnixTime from 'date-fns/getUnixTime'

import config from '@/config'
import { os } from '@/helpers/device.js'
import { ENV_DEV_OR_STAGING, ENV_HACKERONE, VUE_APP_VERSION } from '@/helpers/environment'
import { LS_EXPIRATION_MINUTES } from '@/helpers/constants'

const absmartlySDK = new absmartly.SDK({
	endpoint: `${config('AbSmartlyApiHost')}/v1`,
	apiKey: config('AbSmartlyApiKey'),
	environment: ENV_DEV_OR_STAGING || ENV_HACKERONE ? 'Development' : 'Production',
	application: 'Web',
	timeout: 7000
})

/**
 * Generates an uuid and caches it in `localStorage` for `LS_EXPIRATION_MINUTES` minutes
 * It is necessary for using unit `deviceId` in the A/B Smartly
 */
export function setDeviceId() {
	let deviceId = lscache.get('deviceId')

	if (!deviceId) {
		deviceId = uuid()
		lscache.set('deviceId', deviceId, LS_EXPIRATION_MINUTES)
	}

	window.deviceId = deviceId
}

/**
 * Hash function that converts uuid string to a number from interval
 * @param {string} id - uuid string
 * @returns {number} - number [0..9]
 */
const calcBucket = function (id) {
	if (typeof id !== 'string') {
		return -1
	}

	const hash = stringHash(id)

	return hash % 10
}

/**
 * Function that transforms url param `exp` (i.e.`?exp=web_aa_test1_onboarding~1&exp=web_aa_test2_cancellation~1`) to an object
 * @param {string|string[]} queryParamsExp - value of the `exp` param from store - string or array for multiple
 * @returns {object} - result object, i.e. `{ web_aa_test1_onboarding: 1, web_aa_test2_cancellation: 1 }`
 */
const getOverrideObjectFromUrlParams = function (queryParamsExp) {
	if (typeof queryParamsExp === 'string') {
		queryParamsExp = [queryParamsExp]
	}

	return queryParamsExp.reduce((result, queryParamsString) => {
		const [name, variant] = queryParamsString.split('~')
		const variantNumber = parseInt(variant)

		if (isNaN(variantNumber)) {
			return result
		}

		return { ...result, [name]: variantNumber }
	}, {})
}

export default {
	watch: {
		networkResourceIsReady(value) {
			if (value) {
				this.initAbSmartly()
			}
		}
	},

	computed: {
		...mapState({
			user: 'user',
			subscription: 'subscription'
		}),
		...mapGetters({
			abSmartlyExposures: 'getAbSmartlyExposures',
			getQueryParam: 'location/getQueryParam',
			hostname: 'location/hostname',
			networkResourceIsReady: 'status/networkResourceIsReady',
			isOnboarded: 'isOnboarded',
			countryCode: 'getCountryCode',
			funnelType: 'location/getFunnelType',
			isOrganicTrafficOnboarding: 'userFlags/getIsOrganicTrafficOnboarding'
		}),
		isExperimentsDisabled() {
			const safe = this.getQueryParam('safe')

			return Boolean(safe) || this.isOrganicTrafficOnboarding
		}
	},

	methods: {
		...mapMutations({
			addAbSmartlyExposure: 'addAbSmartlyExposure',
			removeAbSmartlyExposure: 'removeAbSmartlyExposure'
		}),

		...mapActions({
			sendAbSmartlyExposuresToBraze: 'sendAbSmartlyExposuresToBraze'
		}),

		setAbSmartlyAttributes() {
			const utmSource = this.getQueryParam('utm_source')
			const utmCampaign = this.getQueryParam('utm_campaign')
			const utmGender = this.getQueryParam('utm_gender')
			const attributes = {
				device: os,
				device_bucket: calcBucket(window.deviceId),
				deviceId: window.deviceId,
				user_language: window.language,
				app_version: VUE_APP_VERSION,
				hostname: this.hostname
			}

			if (utmSource) {
				attributes.utm_source = utmSource
			}

			if (utmCampaign) {
				attributes.utm_campaign = utmCampaign
			}

			if (utmGender) {
				attributes.utm_gender = utmGender
			}

			this.abSmartlyContext.attributes(attributes)

			this.setAbSmartlyUserAttributes(this.user)
			this.setAbSmartlySubscriptionAttributes(this.subscription)
			this.setAbSmartlyCountryAttributes(this.countryCode)
			this.setAbSmartlyFunnelTypeAttributes(this.funnelType)
		},

		setAbSmartlyUserAttributes(user) {
			if (!user) {
				return
			}

			this.abSmartlyContext.attributes({
				userId: user.id,
				user_bucket: calcBucket(user.id),
				user_created_timestamp: getUnixTime(parseISO(user.createdAt))
			})
		},

		setAbSmartlySubscriptionAttributes(subscription) {
			if (!subscription) {
				return
			}

			this.abSmartlyContext.attributes({
				has_subscription: subscription.period_type === 'normal',
				has_trial: subscription.period_type === 'trial'
			})
		},

		setAbSmartlyCountryAttributes(countryCode) {
			if (!countryCode) {
				return
			}

			this.abSmartlyContext.attributes({
				country: countryCode.toUpperCase()
			})
		},

		setAbSmartlyFunnelTypeAttributes(funnel) {
			if (!funnel) {
				return
			}
			this.abSmartlyContext.attributes({
				funnel_type: funnel.slug
			})
		},

		setAbSmartlyAnalyticsUserProperty() {
			const exposures = this.abSmartlyExposures
			const exposuresList = Object.values(exposures).reduce((list, exposure) => {
				const { name, iteration, variantLetter } = exposure
				const underscoredName = name.replaceAll(' ', '_')

				list.push(`${underscoredName}(${variantLetter})(${`i${iteration}`})`)

				return list
			}, [])

			this.$analytic.setUserProperties({ active_test_groups: exposuresList })
		},

		overrideAbSmartlyTreatmentVariants() {
			const queryParamsExp = this.getQueryParam('exp')

			if (this.isExperimentsDisabled) {
				const experiments = this.getAbSmartlyContextExperiments()
				const overrideObject = experiments.reduce((result, experimentId) => ({ ...result, [experimentId]: 0 }), {})

				this.abSmartlyContext.overrides(overrideObject)
			}

			if (queryParamsExp) {
				this.abSmartlyContext.overrides(getOverrideObjectFromUrlParams(queryParamsExp))
			}
		},

		trackExperimentEnd() {
			const { experiments = [] } = this.abSmartlyContext.data()
			const exposures = this.abSmartlyExposures

			for (const exposureName in exposures) {
				const exposure = exposures[exposureName]
				const experimentData = experiments.find((experiment) => {
					return experiment.name === exposure.name && experiment.iteration === exposure.iteration
				})

				if (!experimentData) {
					this.$analytic.logEvent('Experiment - End', {
						Name: exposure.name,
						Iteration: `i${exposure.iteration}`,
						Variant: exposure.variantLetter,
						VariantName: exposure.variantName
					})

					this.removeAbSmartlyExposure({ name: exposure.name, iteration: exposure.iteration })
				}
			}
		},

		sendAbSmartlyExposureToAnalyticsAndBraze(exposure) {
			const { name, iteration, variantLetter, variantName } = exposure

			this.$analytic.logEvent('Experiment - Exposure', {
				Name: name,
				Iteration: `i${iteration}`,
				Variant: variantLetter,
				VariantName: variantName
			})

			if (this.isOnboarded) {
				this.sendAbSmartlyExposuresToBraze({
					[`${name}_${iteration}`]: exposure
				})
			}
		},

		getAbSmartlyContextExperiments() {
			try {
				return this.abSmartlyContext.experiments()
			} catch (e) {
				return []
			}
		},

		abSmartlyReady() {
			Vue.prototype.$absmartly = this.abSmartlyContext

			this.overrideAbSmartlyTreatmentVariants()
			this.trackExperimentEnd()

			this.$store.commit('status/setResourceReadiness', 'absmartly')
		},

		abSmartlyExposure(data) {
			const { assigned, eligible, fullOn, name, variant } = data

			if (!assigned || !eligible || fullOn) {
				return
			}

			const { experiments } = this.abSmartlyContext.data()
			const variantLetter = String.fromCharCode(65 + variant)
			const experimentData = experiments.find((experiment) => experiment.name === name)

			if (!experimentData) {
				this.abSmartlyError(new Error('Exposured experiment is not in the list of active experiments'))
				return
			}

			const variantName = experimentData.variants[variant]?.name
			const iteration = experimentData.iteration

			this.addAbSmartlyExposure({ name, iteration, variantLetter, variantName })
		},

		abSmartlyError(error) {
			const abSmartlyWarning = ['TimeoutError', 'RetryError'].includes(error.name)

			if (abSmartlyWarning) {
				return
			}

			Sentry.withScope((scope) => {
				scope.setExtras({
					attributes: this.abSmartlyContext?.getAttributes(),
					units: this.abSmartlyContext?.getUnits(),
					experiments: this.getAbSmartlyContextExperiments(),
					exposuresLocalStorage: this.abSmartlyExposures
				})
				scope.setTags({
					abSmartlyError: 'error'
				})
				scope.setTransactionName('A/B Smartly error')
				Sentry.captureException(error)
			})
		},

		abSmartlyEventLogger(context, eventName, data) {
			if (eventName === 'error') {
				this.abSmartlyError(data)
			}

			if (eventName === 'exposure') {
				this.abSmartlyExposure(data)
			}

			if (ENV_DEV_OR_STAGING) {
				/* eslint-disable no-console */
				console.groupCollapsed(`%cA/B Smartly [debug]: %c${eventName}`, 'font-weight: 400', 'color: #669933')
				console.log(context)
				console.log(data)
				console.groupEnd()
				/* eslint-enable no-console */
			}
		},

		initAbSmartly() {
			absmartlySDK.setEventLogger(this.abSmartlyEventLogger)

			this.abSmartlyContext = absmartlySDK.createContext({
				units: { deviceId: window.deviceId }
			})

			if (this.user) {
				this.abSmartlyContext.unit('userId', this.user.id)
			}

			this.setAbSmartlyAttributes()

			this.abSmartlyContext.ready().then(this.abSmartlyReady).catch(this.abSmartlyError)
		}
	},

	created() {
		this.storeObserver = this.$store.subscribe((mutation) => {
			if (!this.abSmartlyContext) {
				return
			}
			if (['addAbSmartlyExposure', 'removeAbSmartlyExposure'].includes(mutation.type)) {
				this.setAbSmartlyAnalyticsUserProperty()

				if (mutation.type === 'addAbSmartlyExposure') {
					this.sendAbSmartlyExposureToAnalyticsAndBraze(mutation.payload)
				}
			} else if (mutation.type === 'setUser') {
				this.setAbSmartlyUserAttributes(mutation.payload)
			} else if (mutation.type === 'setSubscription') {
				this.setAbSmartlySubscriptionAttributes(mutation.payload)
			} else if (mutation.type === 'setGeo') {
				this.setAbSmartlyCountryAttributes(mutation.payload?.countryCode)
			} else if (mutation.type === 'location/setFunnelType') {
				this.setAbSmartlyFunnelTypeAttributes(mutation.payload?.funnelType)
			}
		})
	},

	beforeDestroy() {
		this.storeObserver?.()
	}
}
